import type {
  ChangePasswordPayload,
  RelinkAccessCheckResult,
  RelinkMagicLinkResult,
  RelinkPasswordSetupPayload,
  RelinkRegisterContactPayload,
  RelinkRegistrationOptions,
  RelinkRegistrationProfilePayload,
  RelinkRegistrationProfileResult
} from "~/mocks/relink/backend";
import type {
  AuthResult,
  DeliveryChannel,
  LoginCountry,
  LoginIdentifierType,
  User
} from "~/types";
import { parseISODate, toISODateString } from "~/utils/date";
import {
  cleanDocumentNumber,
  DEFAULT_DOCUMENT_TYPE
} from "~/utils/documentNumber";

// Imported lazily so the fixtures stay out of the production bundle.
const backendMocks = () => import("~/mocks/relink/backend");

export interface UserApiDto {
  id: number;
  role: User["role"];
  company_id?: number;
  company_name?: string;
  names: string;
  paternal_surname: string;
  maternal_surname: string;
  email: string;
  document_number?: string;
  document_type?: string;
  gender?: string;
  custom_gender?: string;
  education_level?: string;
  birthdate?: string | null;
  has_started_survey?: boolean;
  phone?: string;
}

export class UserMapperToDomain implements DataMapper<
  UserApiDto | null,
  User | null
> {
  map(raw: UserApiDto | null): User | null {
    if (!raw) return null;

    return {
      id: raw.id,
      role: raw.role,
      names: raw.names,
      paternalSurname: raw.paternal_surname,
      maternalSurname: raw.maternal_surname,
      email: raw.email,
      documentNumber: raw.document_number ?? "",
      documentType: raw.document_type ?? "",
      gender: raw.gender ?? "",
      customGender: raw.custom_gender,
      birthdate: parseISODate(raw.birthdate),
      educationLevel: raw.education_level ?? "",
      hasStartedSurvey: raw.has_started_survey ?? false,
      companyId: raw.company_id,
      companyName: raw.company_name,
      phone: raw.phone,
      avatarInitials:
        `${raw.names[0] ?? ""}${raw.paternal_surname[0] ?? ""}`.toUpperCase()
    };
  }
}

// Absent keys are omitted, not sent as undefined, so a partial
// update never blanks a field the caller did not touch.
export class UserMapperToApi implements DataMapper<
  Partial<User>,
  Partial<UserApiDto>
> {
  map(user: Partial<User>): Partial<UserApiDto> {
    const payload: Partial<UserApiDto> = {};

    if (user.names !== undefined) payload.names = user.names;
    if (user.paternalSurname !== undefined)
      payload.paternal_surname = user.paternalSurname;
    if (user.maternalSurname !== undefined)
      payload.maternal_surname = user.maternalSurname;
    if (user.email !== undefined) payload.email = user.email;
    if (user.phone !== undefined) payload.phone = user.phone;
    if (user.gender !== undefined) payload.gender = user.gender;
    if (user.customGender !== undefined)
      payload.custom_gender = user.customGender;
    if (user.educationLevel !== undefined)
      payload.education_level = user.educationLevel;
    if (user.documentType !== undefined)
      payload.document_type = user.documentType;
    if (user.documentNumber !== undefined)
      payload.document_number = cleanDocumentNumber(
        user.documentNumber,
        user.documentType
      );
    if (user.birthdate !== undefined)
      payload.birthdate = user.birthdate
        ? toISODateString(user.birthdate)
        : null;

    return payload;
  }
}

// nextPath is only meaningful right after login — session-restore
// only ever reads .user, so it uses UserMapperToDomain directly.
export class AuthResultMapper implements DataMapper<
  UserApiDto | null,
  AuthResult | null
> {
  map(raw: UserApiDto | null): AuthResult | null {
    const user = new UserMapperToDomain().map(raw);
    if (!user) return null;

    return {
      user,
      nextPath:
        user.role === "COMPANYADMIN"
          ? "/admin/company"
          : user.role === "PARTICIPANT"
            ? user.email || user.phone
              ? "/app"
              : "/auth/complete-contact"
            : "/auth/login"
    };
  }
}

export type UserToCreate = Omit<
  User,
  | "id"
  | "role"
  | "avatarInitials"
  | "hasStartedSurvey"
  | "companyId"
  | "companyName"
  | "occupationName"
> & {
  password: string;
  location?: { region: string; city: string };
};

export interface AuthRepository {
  register: (
    user: UserToCreate,
    invitationCode?: string
  ) => Promise<ResponseApi<unknown>>;
  getCurrentUser: (mockUserId?: number) => Promise<ResponseApi<User | null>>;
  login: (
    identifier: string,
    password: string,
    type?: LoginIdentifierType,
    country?: LoginCountry
  ) => Promise<ResponseApi<AuthResult | null>>;
  logout: () => Promise<ResponseApi<boolean>>;
  checkDocumentNumber: (
    documentNumber: string,
    documentType?: string,
    customDocumentType?: string
  ) => Promise<ResponseApi<{ available: boolean } | null>>;
  validateEmail: (code: string) => Promise<ResponseApi<boolean | null>>;
  resendConfirmationEmail: (
    code: string
  ) => Promise<ResponseApi<boolean | null>>;
  changePassword: (
    userId: number,
    payload: ChangePasswordPayload
  ) => Promise<ResponseApi<true | null>>;
  requestPasswordRecovery: (identifier: string) => Promise<
    ResponseApi<{
      expiresInMinutes: number;
      maskedEmail: string;
      testLink: string;
    } | null>
  >;
  resetPassword: (
    password: string,
    token: string
  ) => Promise<ResponseApi<AuthResult | null>>;

  // ── Mock-only (parked) — see types/index.ts's MOCK-ONLY section. ──
  checkAccessByDocument: (
    documentNumber: string
  ) => Promise<ResponseApi<RelinkAccessCheckResult>>;
  registerContact: (
    payload: RelinkRegisterContactPayload
  ) => Promise<ResponseApi<{ passwordSetupToken: string } | null>>;
  completeContactData: (
    payload: RelinkRegisterContactPayload
  ) => Promise<ResponseApi<AuthResult | null>>;
  saveRegistrationProfile: (
    payload: RelinkRegistrationProfilePayload
  ) => Promise<ResponseApi<RelinkRegistrationProfileResult | null>>;
  getRegistrationOptions: () => Promise<ResponseApi<RelinkRegistrationOptions>>;
  createPassword: (
    payload: RelinkPasswordSetupPayload
  ) => Promise<ResponseApi<AuthResult | null>>;
  requestMagicLink: (payload: {
    channel: DeliveryChannel;
    identifier: string;
  }) => Promise<ResponseApi<RelinkMagicLinkResult | null>>;
  requestMagicLinkByDocument: (payload: {
    channel: DeliveryChannel;
    documentNumber: string;
  }) => Promise<ResponseApi<RelinkMagicLinkResult | null>>;
  consumeMagicLink: (payload: {
    identifier: string;
    token: string;
  }) => Promise<ResponseApi<AuthResult | null>>;
}

export const useAuthRepository: () => AuthRepository = () => {
  const { config } = useConfig();

  const register = (user: UserToCreate, invitationCode?: string) => {
    const body = {
      ...new UserMapperToApi().map(user),
      password: user.password,
      user_origin: config.USER_ORIGIN,
      ...(user.location ? { location: user.location } : {}),
      ...(invitationCode ? { invitation_code: invitationCode } : {})
    };

    return useApiClient<unknown>(
      "/auth/register",
      { method: "POST", body },
      async () => {
        const { relinkMockBackend } = await backendMocks();
        const result = relinkMockBackend.registerParticipant(
          body as Parameters<typeof relinkMockBackend.registerParticipant>[0]
        );

        return {
          data: result.data,
          delay: 450,
          message: result.message,
          success: result.success
        };
      }
    );
  };

  const getCurrentUser = (mockUserId?: number) =>
    useApiClient<User | null, UserApiDto | null>(
      "/auth/me",
      { method: "GET", mapper: new UserMapperToDomain() },
      async () => {
        const data = (await backendMocks()).relinkMockBackend.getCurrentUser(
          mockUserId
        );

        return {
          data,
          delay: 120,
          message: data ? "Sesion recuperada." : "La sesion no es valida.",
          success: Boolean(data)
        };
      }
    );

  const logout = () =>
    useApiClient<boolean>("/auth/logout", { method: "POST" }, async () => ({
      data: (await backendMocks()).relinkMockBackend.logout(),
      success: true
    }));

  const getRegistrationOptions = () =>
    useApiClient<RelinkRegistrationOptions>(
      "/auth/registration-options",
      { method: "GET" },
      async () => ({
        data: (await backendMocks()).relinkMockBackend.getRegistrationOptions(),
        delay: 150,
        success: true
      })
    );

  const login = (
    identifier: string,
    password: string,
    type: LoginIdentifierType = "email",
    country?: LoginCountry
  ) => {
    const identificator =
      type === "document_number"
        ? cleanDocumentNumber(identifier, country === "PE" ? "DNI" : undefined)
        : identifier;

    return useApiClient<AuthResult | null, UserApiDto | null>(
      "/auth/login",
      {
        body: { identificator, identificator_type: type, password },
        method: "POST",
        mapper: new AuthResultMapper()
      },
      async () => {
        const result = (await backendMocks()).relinkMockBackend.login(
          identificator,
          password
        );

        return {
          data: result.data,
          delay: 450,
          message: result.message,
          success: result.success
        };
      }
    );
  };

  const checkAccessByDocument = (documentNumber: string) => {
    const cleaned = cleanDocumentNumber(documentNumber);

    return useApiClient<RelinkAccessCheckResult>(
      "/auth/check-document-number",
      {
        body: { document_number: cleaned },
        method: "POST"
      },
      async () => ({
        data: (await backendMocks()).relinkMockBackend.checkAccessByDocument(
          cleaned
        ),
        delay: 400,
        message: "Documento validado.",
        success: true
      })
    );
  };

  const checkDocumentNumber = (
    documentNumber: string,
    documentType: string = DEFAULT_DOCUMENT_TYPE,
    customDocumentType?: string
  ) => {
    const cleaned = cleanDocumentNumber(documentNumber, documentType);

    return useApiClient<{ available: boolean } | null>(
      "/auth/check-document-number",
      {
        method: "POST",
        body: {
          document_number: cleaned,
          document_type: documentType,
          custom_document_type: customDocumentType
        }
      },
      async () => {
        const result = (
          await backendMocks()
        ).relinkMockBackend.checkDocumentNumber(cleaned);

        return {
          data: result.data,
          delay: 400,
          message: result.message,
          success: result.success
        };
      }
    );
  };

  const validateEmail = (code: string) => {
    return useApiClient<boolean | null>(
      "/auth/email-confirmation",
      { method: "POST", params: { code } },
      async () => {
        const result = (await backendMocks()).relinkMockBackend.validateEmail(
          code
        );

        return {
          data: result.data,
          delay: 300,
          message: result.message,
          success: result.success
        };
      }
    );
  };

  const resendConfirmationEmail = (code: string) => {
    return useApiClient<boolean | null>(
      "/auth/resend-confirmation",
      { method: "POST", body: { code } },
      async () => {
        const result = (
          await backendMocks()
        ).relinkMockBackend.resendConfirmationEmail(code);

        return {
          data: result.data,
          delay: 300,
          message: result.message,
          success: result.success
        };
      }
    );
  };

  const registerContact = (payload: RelinkRegisterContactPayload) => {
    return useApiClient<{ passwordSetupToken: string } | null>(
      "/auth/register",
      {
        body: payload,
        method: "POST"
      },
      async () => {
        const result = (await backendMocks()).relinkMockBackend.registerContact(
          payload
        );

        return {
          data: result.data,
          delay: 450,
          message: result.message,
          success: result.success
        };
      }
    );
  };

  const completeContactData = (payload: RelinkRegisterContactPayload) => {
    return useApiClient<AuthResult | null>(
      "/auth/contact-data",
      {
        body: payload,
        method: "POST"
      },
      async () => {
        const result = (
          await backendMocks()
        ).relinkMockBackend.completeContactData(payload);

        return {
          data: result.data,
          delay: 450,
          message: result.message,
          success: result.success
        };
      }
    );
  };

  const saveRegistrationProfile = (
    payload: RelinkRegistrationProfilePayload
  ) => {
    return useApiClient<RelinkRegistrationProfileResult | null>(
      "/auth/register/profile",
      {
        body: payload,
        method: "POST"
      },
      async () => {
        const result = (
          await backendMocks()
        ).relinkMockBackend.saveRegistrationProfile(payload);

        return {
          data: result.data,
          delay: 450,
          message: result.message,
          success: result.success
        };
      }
    );
  };

  const createPassword = (payload: RelinkPasswordSetupPayload) => {
    return useApiClient<AuthResult | null>(
      "/auth/reset-password",
      {
        body: payload,
        method: "POST"
      },
      async () => {
        const result = (await backendMocks()).relinkMockBackend.createPassword(
          payload
        );

        return {
          data: result.data,
          delay: 450,
          message: result.message,
          success: result.success
        };
      }
    );
  };

  const changePassword = (userId: number, payload: ChangePasswordPayload) => {
    return useApiClient<true | null>(
      "/auth/update-password",
      {
        body: {
          oldPassword: payload.oldPassword,
          newPassword: payload.newPassword
        },
        method: "POST"
      },
      async () => {
        const result = (await backendMocks()).relinkMockBackend.changePassword(
          userId,
          payload
        );

        return {
          data: result.data,
          delay: 450,
          message: result.message,
          success: result.success
        };
      }
    );
  };

  const requestMagicLink = (payload: {
    channel: DeliveryChannel;
    identifier: string;
  }) => {
    return useApiClient<RelinkMagicLinkResult | null>(
      "/auth/email-confirmation",
      {
        body: payload,
        method: "POST"
      },
      async () => {
        const result = (
          await backendMocks()
        ).relinkMockBackend.requestMagicLink(payload);

        return {
          data: result.data,
          delay: 400,
          message: result.message,
          success: result.success
        };
      }
    );
  };

  const requestMagicLinkByDocument = (payload: {
    channel: DeliveryChannel;
    documentNumber: string;
  }) => {
    const cleaned = cleanDocumentNumber(payload.documentNumber);

    return useApiClient<RelinkMagicLinkResult | null>(
      "/auth/magic-link",
      {
        body: {
          channel: payload.channel,
          document_number: cleaned
        },
        method: "POST"
      },
      async () => {
        const result = (
          await backendMocks()
        ).relinkMockBackend.requestMagicLinkByDocument({
          ...payload,
          documentNumber: cleaned
        });

        return {
          data: result.data,
          delay: 400,
          message: result.message,
          success: result.success
        };
      }
    );
  };

  const consumeMagicLink = (payload: { identifier: string; token: string }) => {
    return useApiClient<AuthResult | null>(
      "/auth/magic-link/consume",
      {
        body: payload,
        method: "POST"
      },
      async () => {
        const result = (
          await backendMocks()
        ).relinkMockBackend.consumeMagicLink(payload);

        return {
          data: result.data,
          delay: 450,
          message: result.message,
          success: result.success
        };
      }
    );
  };

  const requestPasswordRecovery = (identifier: string) => {
    return useApiClient<{
      expiresInMinutes: number;
      maskedEmail: string;
      testLink: string;
    } | null>(
      "/auth/forgot-password",
      {
        body: { email: identifier, user_origin: config.USER_ORIGIN },
        method: "POST"
      },
      async () => {
        const result = (
          await backendMocks()
        ).relinkMockBackend.requestPasswordRecovery(identifier);

        return {
          data: result.data,
          delay: 400,
          message: result.message,
          success: result.success
        };
      }
    );
  };

  const resetPassword = (password: string, token: string) => {
    return useApiClient<AuthResult | null>(
      "/auth/reset-password",
      {
        body: {
          code: token,
          password,
          passwordConfirmation: password,
          user_origin: config.USER_ORIGIN
        },
        method: "POST"
      },
      async () => {
        const result = (await backendMocks()).relinkMockBackend.resetPassword(
          token,
          password
        );

        return {
          data: result.data,
          delay: 450,
          message: result.message,
          success: result.success
        };
      }
    );
  };

  return {
    changePassword,
    checkAccessByDocument,
    checkDocumentNumber,
    completeContactData,
    consumeMagicLink,
    createPassword,
    getCurrentUser,
    getRegistrationOptions,
    login,
    logout,
    register,
    registerContact,
    requestMagicLink,
    requestMagicLinkByDocument,
    requestPasswordRecovery,
    resendConfirmationEmail,
    saveRegistrationProfile,
    resetPassword,
    validateEmail
  };
};
